
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell, LogOut } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useEffect } from "react";
import { devLog, devError } from "@/utils/devLog";
import { toast } from "sonner";
import { useAuth } from "@/providers/AuthProvider";
import type { Profile } from "@/types/profile";

interface SidebarUserProfileProps {
  notifications?: number;
}

export function SidebarUserProfile({ notifications = 0 }: SidebarUserProfileProps) {
  const queryClient = useQueryClient();
  const { logout, user } = useAuth();
  
  // Fix: properly type-cast the profile data from the query cache
  const profile = queryClient.getQueryData<Profile>(['userProfile', user?.id]) || null;

  useEffect(() => {
    const handleFocus = () => {
      devLog("[SidebarUserProfile] ➡️ Window focused, refreshing profile data");
      queryClient.invalidateQueries({ queryKey: ['userProfile', user?.id] });
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [queryClient, user?.id]);

  useEffect(() => {
    devLog("[SidebarUserProfile] Component mounted/updated", { 
      hasProfile: !!profile,
      role: profile?.role,
      username: profile?.username 
    });
  }, [profile]);

  const userName = profile?.full_name || user?.user_metadata?.name || user?.user_metadata?.full_name || 'Student';
  const userRole = profile?.role || 'student';

  const getAvatarFallback = () => {
    if (userName && userName.length > 0) {
      return userName[0].toUpperCase();
    }
    return 'S';
  };

  const handleSignOut = async () => {
    try {
      devLog("[SidebarUserProfile] ➡️ Signing out user");
      await logout();
      queryClient.clear(); // Clear all queries
      toast.success("Successfully signed out");
      // Note: Navigation is now handled in the AuthProvider
    } catch (error) {
      devError("[SidebarUserProfile] ❌ Error signing out:", error);
      toast.error("Error signing out");
    }
  };
  
  return (
    <div className="mt-auto pt-4 border-t">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <Avatar>
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback>{getAvatarFallback()}</AvatarFallback>
            </Avatar>
          </div>
          <div className="flex-1">
            <p className="font-medium">{userName}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center">
          <ThemeToggle className="h-8 w-8" />
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 relative"
          >
            <Bell size={16} />
            {notifications > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-xs flex items-center justify-center rounded-full">
                {notifications}
              </span>
            )}
          </Button>
        </div>
      </div>
      
      <Button 
        variant="outline" 
        className="w-full justify-start gap-2 mt-2"
        onClick={handleSignOut}
      >
        <LogOut size={16} />
        Sign Out
      </Button>
    </div>
  );
}
